import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiMenu,
  FiX,
  FiChevronDown,
  FiPhone,
  FiMail,
  FiClock,
  FiBook,
  FiUsers,
  FiActivity,
  FiAward,
  FiHome,
  FiBell,
  FiBarChart2,
  FiCalendar,
  FiMessageSquare,
  FiExternalLink,
} from "react-icons/fi";
import { MdSchool } from "react-icons/md";
import { settingsAPI, noticeAPI } from "../../services/api";

/* ── data ──────────────────────────────────────────────────────────────── */
const mainNav = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about" },
  {
    label: "Academics",
    path: "/academics",
    children: [
      {
        label: "Primary School",
        path: "/academics#primary",
        icon: FiBook,
        desc: "Grade 1 – 5",
      },
      {
        label: "Secondary School",
        path: "/academics#secondary",
        icon: FiBook,
        desc: "Grade 6 – 10",
      },
      {
        label: "High School",
        path: "/academics#highschool",
        icon: FiAward,
        desc: "Grade 11 – 12",
      },
    ],
  },
  {
    label: "Admissions",
    path: "/admissions",
    children: [
      {
        label: "Apply Online",
        path: "/admissions/apply",
        icon: FiExternalLink,
        desc: "Start your application",
      },
      {
        label: "Admission Process",
        path: "/admissions#process",
        icon: FiActivity,
        desc: "Step-by-step guide",
      },
      {
        label: "Tuition & Fees",
        path: "/admissions#fees",
        icon: FiBook,
        desc: "Fee structure details",
      },
      {
        label: "Scholarships",
        path: "/admissions#scholarships",
        icon: FiAward,
        desc: "Financial assistance",
      },
    ],
  },
  {
    label: "Student Life",
    path: "/student-life",
    children: [
      {
        label: "Clubs & Societies",
        path: "/student-life#clubs",
        icon: FiUsers,
        desc: "Join a community",
      },
      {
        label: "Sports",
        path: "/student-life#sports",
        icon: FiActivity,
        desc: "Athletics & fitness",
      },
      {
        label: "Community Activities",
        path: "/student-life#community",
        icon: FiHome,
        desc: "Outreach programs",
      },
      {
        label: "Facilities",
        path: "/student-life#facilities",
        icon: FiHome,
        desc: "Campus resources",
      },
    ],
  },
  { label: "News & Events", path: "/news-events" },
  { label: "Gallery", path: "/gallery" },
];

const quickLinks = [
  { label: "Notices", path: "/notices", icon: FiBell },
  { label: "Results", path: "/results", icon: FiBarChart2 },
  { label: "Calendar", path: "/news-events", icon: FiCalendar },
  { label: "Contact", path: "/contact", icon: FiMessageSquare },
];

/* ── dropdown ──────────────────────────────────────────────────────────── */
function Dropdown({ items, isOpen, parentPath, onClose }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.97 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          className="absolute top-[calc(100%+8px)] left-0 min-w-[250px] bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.13)] border border-gray-100 overflow-hidden z-50"
        >
          {/* red accent top bar */}
          <div className="h-1 w-full bg-gradient-to-r from-primary-500 to-secondary-500" />
          <div className="py-2">
            {items.map((item, i) => (
              <Link
                key={i}
                to={item.path}
                onClick={onClose}
                className="flex items-start gap-3 px-4 py-3 hover:bg-primary-50 transition-colors group"
              >
                <span className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-primary-50 group-hover:bg-primary-500 flex items-center justify-center transition-colors">
                  <item.icon className="w-4 h-4 text-primary-500 group-hover:text-white transition-colors" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-gray-800 group-hover:text-primary-600 transition-colors leading-tight">
                    {item.label}
                  </span>
                  {item.desc && (
                    <span className="block text-xs text-gray-400 mt-0.5">
                      {item.desc}
                    </span>
                  )}
                </span>
              </Link>
            ))}
          </div>
          <div className="border-t border-gray-100 px-4 py-2.5 bg-gray-50">
            <Link
              to={parentPath}
              onClick={onClose}
              className="flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-secondary-600 transition-colors"
            >
              View all <FiChevronDown className="w-3 h-3 -rotate-90" />
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ── navbar ────────────────────────────────────────────────────────────── */
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);

  const [logoUrl, setLogoUrl] = useState(null);
  const [urgentNotices, setUrgentNotices] = useState([]);
  const [noticeIdx, setNoticeIdx] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const wrapRef = useRef(null);
  const hoverTimer = useRef(null);

  useEffect(() => {
    settingsAPI
      .get()
      .then((res) => {
        const url = res.data?.data?.settings?.logo?.url;
        if (url) setLogoUrl(url);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    noticeAPI
      .getAll({ limit: 100 })
      .then((res) => {
        const allNotices = res.data?.data?.notices || res.data?.notices || [];
        setUrgentNotices(allNotices);
      })
      .catch((err) => {
        console.error(
          "Error fetching notices:",
          err?.response?.data || err?.message,
        );
      });
  }, []);

  useEffect(() => {
    if (urgentNotices.length === 0) return;
    const timer = setInterval(() => {
      setNoticeIdx((prev) => (prev + 1) % urgentNotices.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [urgentNotices.length]);

  useEffect(() => {
    setMobileOpen(false);
    setMobileExpanded(null);
  }, [location.pathname]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const fn = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setActiveIdx(null);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const enter = (i) => {
    clearTimeout(hoverTimer.current);
    setActiveIdx(i);
  };
  const leave = () => {
    hoverTimer.current = setTimeout(() => setActiveIdx(null), 120);
  };

  const linkCls = (active) => {
    const base =
      "px-2.5 py-2 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap ";
    if (active)
      return (
        base +
        (scrolled ? "text-primary-600 bg-primary-50" : "text-white bg-white/20")
      );
    return (
      base +
      (scrolled
        ? "text-gray-600 hover:text-primary-600 hover:bg-primary-50"
        : "text-white/85 hover:text-white hover:bg-white/10")
    );
  };

  return (
    <>
      <header
        ref={wrapRef}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? "shadow-[0_2px_24px_rgba(0,0,0,0.12)]" : ""
        }`}
      >
        {/* ══ TOP BAR ═══════════════════════════════════════════════════════ */}
        <div
          className={`hero-gradient transition-all duration-300 overflow-hidden ${
            scrolled ? "max-h-0 opacity-0" : "max-h-10 sm:max-h-12 opacity-100"
          }`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 sm:h-10 flex items-center justify-between">
            {/* left — contact */}
            <div className="hidden sm:flex items-center gap-5 text-white text-xs">
              <span className="flex items-center gap-1.5">
                <FiPhone className="w-3 h-3 text-accent-400" />
                +977-1-4123456
              </span>
              <span className="flex items-center gap-1.5">
                <FiMail className="w-3 h-3 text-accent-400" />
                info@maria.edu.np
              </span>
              <span className="flex items-center gap-1.5">
                <FiClock className="w-3 h-3 text-accent-400" />
                Sun – Fri &nbsp;7:00 AM – 5:00 PM
              </span>
            </div>

            {/* right — quick links */}
            <div className="flex items-center gap-0.5 ml-auto overflow-hidden">
              {quickLinks.map((ql, i) => (
                <NavLink
                  key={i}
                  to={ql.path}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      isActive
                        ? "bg-white/20 text-white"
                        : "text-white/85 hover:text-white hover:bg-white/15"
                    }`
                  }
                >
                  <ql.icon className="w-3 h-3" />
                  <span className="hidden md:inline">{ql.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>

        {/* ══ MAIN NAV ══════════════════════════════════════════════════════ */}
        <div
          className={`transition-all duration-300 ${
            scrolled
              ? "bg-white py-3 border-b border-gray-100"
              : "hero-gradient py-4"
          }`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-6">
              {/* logo */}
              <Link to="/" className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0">
                {logoUrl ? (
                  <div
                    className={`flex items-center justify-center rounded-xl sm:rounded-2xl transition-all overflow-hidden shrink-0 ${
                      scrolled
                        ? "bg-white border border-gray-100 shadow-sm p-1"
                        : "bg-white/95 shadow-lg p-1.5 sm:p-2"
                    }`}
                    style={{ minWidth: 44, minHeight: 44 }}
                  >
                    <img
                      src={logoUrl}
                      alt="School Logo"
                      className="h-9 sm:h-12 lg:h-14 w-auto max-w-[120px] sm:max-w-[160px] lg:max-w-[190px] object-contain block"
                    />
                  </div>
                ) : (
                  <div
                    className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shadow-sm transition-all shrink-0 ${
                      scrolled
                        ? "bg-primary-500"
                        : "bg-white/15 ring-1 ring-white/25"
                    }`}
                  >
                    <MdSchool className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                )}
                <div className="min-w-0">
                  <p
                    className={`font-serif font-bold text-xs sm:text-sm leading-snug transition-colors max-w-[130px] sm:max-w-[180px] ${
                      scrolled ? "text-primary-700" : "text-white"
                    }`}
                  >
                    Maria Education Foundation
                  </p>
                  <p
                    className={`text-[9px] sm:text-[10px] font-semibold uppercase tracking-widest transition-colors ${
                      scrolled ? "text-secondary-500" : "text-white/75"
                    }`}
                  >
                    Inspiring Excellence
                  </p>
                </div>
              </Link>

              {/* desktop links */}
              <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
                {mainNav.map((link, i) => (
                  <div
                    key={i}
                    className="relative"
                    onMouseEnter={() => link.children && enter(i)}
                    onMouseLeave={() => link.children && leave()}
                  >
                    {link.children ? (
                      <>
                        <button
                          onClick={() => navigate(link.path)}
                          className={
                            linkCls(false) + " flex items-center gap-1"
                          }
                        >
                          {link.label}
                          <FiChevronDown
                            className={`w-3.5 h-3.5 transition-transform duration-200 ${
                              activeIdx === i ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        <Dropdown
                          items={link.children}
                          isOpen={activeIdx === i}
                          parentPath={link.path}
                          onClose={() => setActiveIdx(null)}
                        />
                      </>
                    ) : (
                      <NavLink
                        to={link.path}
                        end={link.path === "/"}
                        className={({ isActive }) => linkCls(isActive)}
                      >
                        {link.label}
                      </NavLink>
                    )}
                  </div>
                ))}
              </nav>

              {/* CTA */}
              <div className="hidden lg:flex items-center gap-2 shrink-0">
                <Link
                  to="/admissions/apply"
                  className="px-5 py-2.5 bg-accent-500 hover:bg-accent-600 text-white rounded-xl text-sm font-bold shadow-sky-glow hover:shadow-none transition-all hover:-translate-y-0.5"
                >
                  Apply Now
                </Link>
              </div>

              {/* mobile hamburger */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Toggle menu"
                className={`lg:hidden p-2.5 rounded-xl transition-colors ${
                  scrolled
                    ? "text-primary-600 hover:bg-primary-50"
                    : "text-white hover:bg-white/10"
                }`}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {mobileOpen ? (
                    <motion.span
                      key="x"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <FiX className="w-6 h-6" />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="m"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <FiMenu className="w-6 h-6" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>
            </div>
          </div>
        </div>

        {/* ══ MOBILE MENU ═══════════════════════════════════════════════════ */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden bg-white border-t border-gray-100 shadow-2xl"
            >
              <div className="max-h-[80vh] overflow-y-auto">
                {/* main links */}
                <div className="px-4 pt-4 pb-2 space-y-0.5">
                  {mainNav.map((link, i) => (
                    <div key={i}>
                      {link.children ? (
                        <>
                          <button
                            onClick={() =>
                              setMobileExpanded(mobileExpanded === i ? null : i)
                            }
                            className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                          >
                            {link.label}
                            <FiChevronDown
                              className={`w-4 h-4 transition-transform duration-200 ${
                                mobileExpanded === i
                                  ? "rotate-180 text-primary-500"
                                  : "text-gray-400"
                              }`}
                            />
                          </button>
                          <AnimatePresence>
                            {mobileExpanded === i && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="ml-4 mb-2 space-y-0.5 border-l-2 border-secondary-200 pl-3">
                                  {link.children.map((child, j) => (
                                    <Link
                                      key={j}
                                      to={child.path}
                                      onClick={() => setMobileOpen(false)}
                                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                                    >
                                      <child.icon className="w-4 h-4 text-secondary-500 shrink-0" />
                                      <span>
                                        <span className="block font-medium leading-tight">
                                          {child.label}
                                        </span>
                                        {child.desc && (
                                          <span className="block text-xs text-gray-400">
                                            {child.desc}
                                          </span>
                                        )}
                                      </span>
                                    </Link>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      ) : (
                        <NavLink
                          to={link.path}
                          end={link.path === "/"}
                          onClick={() => setMobileOpen(false)}
                          className={({ isActive }) =>
                            `block px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                              isActive
                                ? "text-primary-600 bg-primary-50"
                                : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                            }`
                          }
                        >
                          {link.label}
                        </NavLink>
                      )}
                    </div>
                  ))}
                </div>

                {/* quick access */}
                <div className="px-4 py-4 border-t border-gray-100 bg-gray-50">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-1 mb-3">
                    Quick Access
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {quickLinks.map((ql, i) => (
                      <NavLink
                        key={i}
                        to={ql.path}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          `flex flex-col items-center gap-1.5 py-3 rounded-xl text-xs font-medium transition-colors ${
                            isActive
                              ? "bg-secondary-500 text-white"
                              : "bg-white text-gray-600 hover:bg-primary-50 hover:text-primary-600 border border-gray-100 shadow-sm"
                          }`
                        }
                      >
                        <ql.icon className="w-4 h-4" />
                        {ql.label}
                      </NavLink>
                    ))}
                  </div>
                </div>

                {/* CTAs */}
                <div className="px-4 py-4 border-t border-gray-100">
                  <Link
                    to="/admissions/apply"
                    onClick={() => setMobileOpen(false)}
                    className="w-full flex items-center justify-center py-3 bg-accent-500 hover:bg-accent-600 text-white rounded-xl text-sm font-bold transition-colors"
                  >
                    Apply Now
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ══ NOTICES BANNER ════════════════════════════════════════════════ */}
      <AnimatePresence>
        {urgentNotices.length > 0 && (
          <motion.div
            key="notice-banner"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed top-0 left-0 right-0 z-[49] bg-gradient-to-r from-blue-600 to-indigo-600 overflow-hidden"
            style={{ marginTop: "var(--navbar-height, 0px)" }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 py-3 text-white">
                <FiBell className="w-5 h-5 flex-shrink-0 animate-bounce" />
                <div className="flex-1 min-w-0">
                  <Link
                    to="/notices"
                    className="font-semibold hover:underline line-clamp-1"
                  >
                    📢 {urgentNotices[noticeIdx]?.title}
                  </Link>
                </div>
                <button
                  onClick={() =>
                    setNoticeIdx((prev) => (prev + 1) % urgentNotices.length)
                  }
                  className="text-white/80 hover:text-white transition-colors flex-shrink-0"
                >
                  <FiChevronDown className="w-5 h-5 rotate-180" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
